import { Link } from 'react-router-dom';
import Hero from './components/Hero';
import MovieCard from '@/components/feature/MovieCard';
import { getHotMovies, getNewMovies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

function SectionHeader({ title, subtitle, linkTo }: { title: string; subtitle?: string; linkTo?: string }) {
  return (
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>
        )}
      </div>
      {linkTo && (
        <Link
          to={linkTo}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer no-underline transition-colors"
        >
          查看全部 <i className="ri-arrow-right-line ml-1" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();

  return (
    <>
      <Hero />

      <main>
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto">
            <SectionHeader
              title="热门推荐"
              subtitle="大家都在看的精彩内容"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
              {hotMovies.map((movie: Movie) => (
                <MovieCard key={movie.id} movie={movie} size="small" />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <SectionHeader
              title="内容分类"
              subtitle="按类型发现你感兴趣的内容"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer no-underline"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200">
                    <i className={`${cat.icon} text-xl`} />
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto">
            <SectionHeader
              title="最新更新"
              subtitle="每日更新，精彩不断"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
              {newMovies.map((movie: Movie) => (
                <MovieCard key={movie.id} movie={movie} size="small" />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                为什么选择蚂蚁影视？
              </h2>
              <p className="text-sm text-foreground-500 mb-8">
                高清流畅、免费观看、即点即播，数千万用户的共同选择
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-4k-line', title: '超清画质', desc: '支持4K高清播放，影院级视听享受' },
                { icon: 'ri-flashlight-line', title: '即点即播', desc: '无需等待缓冲，流畅观影体验' },
                { icon: 'ri-refresh-line', title: '每日更新', desc: '海量资源每日上新，追剧永不停歇' },
                { icon: 'ri-shield-check-line', title: '完全免费', desc: '无需会员，无需付费，随意观看' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center text-center p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-2xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
              开始你的追剧之旅
            </h2>
            <p className="text-sm text-foreground-500 mb-6 max-w-lg mx-auto">
              加入蚂蚁影视，畅享海量高清影视资源，随时随地在线追剧不卡顿
            </p>
            <Link
              to="/category/movie"
              className="inline-flex items-center px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer no-underline"
            >
              立即探索 <i className="ri-arrow-right-line ml-2" />
            </Link>
          </div>
        </section>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '蚂蚁影视',
            url: 'https://www.mayifilm.com/',
            description: '蚂蚁影视高清免费在线观看平台，即点即播不缓冲，海量电影电视剧每日更新，流畅追剧首选。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.mayifilm.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '蚂蚁影视',
            url: 'https://www.mayifilm.com/',
            description: '蚂蚁影视高清免费在线观看平台',
          }),
        }}
      />
    </>
  );
}